import React, { useMemo, useCallback, FC } from 'react';
import {
    useTheme,
    useMediaQuery,
    Grid,
    Box,
    Typography,
    Popper,
    Collapse,
    Paper,
    TextField,
    IconButton,
} from '@maas-components/core';
import { Close } from '@maas-components/icons';
import SearchItem from './SearchItem';
import useStyles from './styles';
import { EmptyState } from '../../svgs/emptyState';
import { Service } from '../../../types';

type SearchContainerProps = {
    recentServices: string[];
    services: Service[];
    handleServiceClick: (serviceName: string) => void;
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    searchValue: string;
    handleSearchTextChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    popperAnchor: React.MutableRefObject<HTMLElement | null>;
    mode: 'dark' | 'light';
};

const SearchContainer: FC<SearchContainerProps> = ({
    handleServiceClick,
    services,
    isOpen,
    setIsOpen,
    searchValue,
    handleSearchTextChange,
    popperAnchor,
    mode,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const classes = useStyles({ mode });

    const handleClose = () => {
        setIsOpen(false);
    };

    const handleSearchItemClick = useCallback(
        (serviceName: string) => {
            setIsOpen(false);
            handleServiceClick(serviceName);
        },
        [handleServiceClick],
    );

    const renderServices = useMemo(() => {
        const filteredServices = services.filter((service) =>
            service.name.toLowerCase().includes(searchValue.toLowerCase()),
        );

        if (filteredServices.length > 0) {
            return filteredServices.slice(0, 4).map((service) => (
                <Grid key={service.name} item xs={12} sm={3}>
                    <SearchItem mode={mode} onClick={() => handleSearchItemClick(service.name)} service={service} />
                </Grid>
            ));
        }

        return (
            <Box display="flex" alignItems="center" justifyContent="center" flex={1} flexDirection="column" pb={2}>
                <EmptyState />
                <Typography>Nenhum serviço encontrado</Typography>
            </Box>
        );
    }, [searchValue, handleSearchItemClick, services]);

    if (!popperAnchor.current) {
        return null;
    }

    return (
        <Popper
            open={isOpen}
            style={{
                zIndex: theme.zIndex.modal,
                width: isMobile ? '100%' : popperAnchor.current.clientWidth,
            }}
            anchorEl={isMobile ? undefined : popperAnchor.current}
            transition
            keepMounted={!isMobile}
        >
            {({ TransitionProps }) => (
                <Collapse {...TransitionProps} timeout={200}>
                    <Paper className={classes.paper} elevation={1} square={isMobile}>
                        {isMobile && (
                            <Box p={1}>
                                <TextField
                                    autoFocus
                                    hiddenLabel
                                    variant="outlined"
                                    placeholder="Procure por aplicativos ou serviços"
                                    className={classes.searchField}
                                    onChange={handleSearchTextChange}
                                    margin="dense"
                                    size="small"
                                    InputProps={{
                                        autoComplete: 'off',
                                        className: classes.searchFieldInput,
                                    }}
                                />
                            </Box>
                        )}
                        <Box p={2}>
                            <Grid container spacing={isMobile ? 0 : 2}>
                                {isMobile && (
                                    <Box position="absolute" right={0} top={8}>
                                        <IconButton onClick={handleClose}>
                                            <Close titleAccess="fechar busca" />
                                        </IconButton>
                                    </Box>
                                )}
                                <>{renderServices}</>
                            </Grid>
                        </Box>
                    </Paper>
                </Collapse>
            )}
        </Popper>
    );
};

export default SearchContainer;
